<script setup>
import { inject } from 'vue';

const model = defineModel({
  type: Object,
  default: () => ({})
});

const emit = defineEmits(['editStep']);

const personalInfo = inject('personalInfo');
const vehicleInfo = inject('vehicleInfo');
const documentsInfo = inject('documentsInfo');
const stepIndex = inject('stepIndex');


function goToStep(index) {
  emit('editStep', index);
}
</script>

<template>
  <div class="signup-step signup-step-review">
    <div class="row g-4">
      <div class="col-12">
        <h5 class="text-primary mb-3">Review Your Information</h5>
        <p class="text-muted">Please review all your information before submitting your registration.</p>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="fa-solid fa-user me-2"></i>Personal Information</h6>
            <button type="button" class="btn btn-link btn-sm p-0" @click="goToStep(stepIndex.PERSONAL)">
              <i class="fa-solid fa-pen"></i> Edit
            </button>
          </div>
          <div class="card-body">
            <dl class="row mb-0">
              <dt class="col-sm-4">Name</dt>
              <dd class="col-sm-8">{{ personalInfo?.firstName }} {{ personalInfo?.lastName }}</dd>

              <dt class="col-sm-4">Email</dt>
              <dd class="col-sm-8">{{ personalInfo?.email }}</dd>

              <dt class="col-sm-4">Phone</dt>
              <dd class="col-sm-8">{{ personalInfo?.countryCode }}{{ personalInfo?.phoneNumber }}</dd>

              <dt class="col-sm-4">Date of Birth</dt>
              <dd class="col-sm-8">{{ personalInfo?.dateOfBirth }}</dd>

              <dt class="col-sm-4">Address</dt>
              <dd class="col-sm-8">
                {{ personalInfo?.address }}<br>
                {{ personalInfo?.city }}, {{ personalInfo?.state }} {{ personalInfo?.postalCode }}<br>
                {{ personalInfo?.country }}
              </dd>
            </dl>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="fa-solid fa-car me-2"></i>Vehicle Information</h6>
            <button type="button" class="btn btn-link btn-sm p-0" @click="goToStep(stepIndex.VEHICLE)">
              <i class="fa-solid fa-pen"></i> Edit
            </button>
          </div>
          <div class="card-body">
            <dl class="row mb-0">
              <dt class="col-sm-4">Make</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.make }}</dd>

              <dt class="col-sm-4">Model</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.model }}</dd>

              <dt class="col-sm-4">Year</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.year }}</dd>

              <dt class="col-sm-4">Color</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.color }}</dd>

              <dt class="col-sm-4">Plate Number</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.plateNumber }}</dd>

              <dt class="col-sm-4">Odometer</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.odometer ? vehicleInfo.odometer.toLocaleString() + ' km' : 'Not set' }}</dd>

              <dt class="col-sm-4">Vehicle Type</dt>
              <dd class="col-sm-8">{{ vehicleInfo?.vehicleType }}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="fa-solid fa-file me-2"></i>Documents</h6>
            <button type="button" class="btn btn-link btn-sm p-0" @click="goToStep(stepIndex.DOCUMENTS)">
              <i class="fa-solid fa-pen"></i> Edit
            </button>
          </div>
          <div class="card-body">
            <dl class="row mb-0">
              <dt class="col-sm-6">License (Front)</dt>
              <dd class="col-sm-6">
                <span v-if="documentsInfo?.driverLicenseFront || documentsInfo?.hasLicenseDocument" class="text-success">
                  <i class="fa-solid fa-check-circle"></i>
                  {{ documentsInfo?.driverLicenseFront?.name || 'Uploaded' }}
                </span>
                <span v-else class="text-warning">
                  <i class="fa-solid fa-exclamation-circle"></i> Not uploaded
                </span>
              </dd>

              <dt class="col-sm-6">License (Back)</dt>
              <dd class="col-sm-6">
                <span v-if="documentsInfo?.driverLicenseBack || documentsInfo?.hasLicenseDocument" class="text-success">
                  <i class="fa-solid fa-check-circle"></i>
                  {{ documentsInfo?.driverLicenseBack?.name || 'Uploaded' }}
                </span>
                <span v-else class="text-warning">
                  <i class="fa-solid fa-exclamation-circle"></i> Not uploaded
                </span>
              </dd>

              <dt class="col-sm-6">License Expiry</dt>
              <dd class="col-sm-6">{{ documentsInfo?.licenseExpiryDate || 'Not set' }}</dd>

              <dt class="col-sm-6">Insurance Certificate (Pink Slip)</dt>
              <dd class="col-sm-6">
                <span v-if="documentsInfo?.hasInsuranceDocument" class="text-success">
                  <i class="fa-solid fa-check-circle"></i> Uploaded
                </span>
                <span v-else class="text-warning">
                  <i class="fa-solid fa-exclamation-circle"></i> Not uploaded
                </span>
              </dd>

              <dt class="col-sm-6">Insurance Expiry</dt>
              <dd class="col-sm-6">{{ documentsInfo?.insuranceExpiryDate || 'Not set' }}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="fa-solid fa-shield-check me-2"></i>Agreements</h6>
          </div>
          <div class="card-body">
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="agreeToBackgroundCheck" v-model="model.agreedToBackgroundCheck">
              <label class="form-check-label" for="agreeToBackgroundCheck">
                I authorize Asher Rides to conduct a background check including criminal history and motor vehicle record (MVR) verification. I understand this is required for approval.
              </label>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="agreeToEligibility" v-model="model.agreedToEligibility">
              <label class="form-check-label" for="agreeToEligibility">
                I confirm I am at least 25 years old and legally eligible to work in Canada as an independent contractor.
              </label>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="agreeToTerms" v-model="model.agreedToTerms">
              <label class="form-check-label" for="agreeToTerms">
                I have read and agree to the <a href="https://asherrides.notion.site/terms-and-conditions?pvs=143" target="_blank">Terms of Service</a>,
                <a href="https://asherrides.notion.site/privacy-policy?pvs=143" target="_blank">Privacy Policy</a>,
                and <a href="https://asherrides.notion.site/independent-contractor-driver-policy?pvs=74" target="_blank">Independent Contractor Agreement</a>.
              </label>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="agreeToAccuracy" v-model="model.agreedToAccuracy">
              <label class="form-check-label" for="agreeToAccuracy">
                I confirm all information provided is accurate and complete.
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
dl dt {
  font-weight: 500;
}
dl dd {
  margin-bottom: 0.5rem;
}
</style>
